import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const EDGES = [3, 4, 5, 6, 7, 8, 9, 10];

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function sqrtLatex(coef, radicand) {
  if (radicand === 1) return String(coef);
  if (coef === 1) return `\\sqrt{${radicand}}`;
  return `${coef}\\sqrt{${radicand}}`;
}

function makeDistractors(a) {
  const correct = sqrtLatex(a, 3);
  const coefficients = [a];
  const rootA = Math.sqrt(a);

  if (Number.isInteger(rootA) && rootA >= 2) {
    coefficients.push(rootA);
  }
  if (a <= 7) {
    coefficients.push(a * a);
  }
  if (a >= 8 && !coefficients.includes(3)) {
    coefficients.push(3);
  }

  const uniqueCoefs = [...new Set(coefficients)].slice(0, 2);
  const pairOptions = uniqueCoefs.flatMap((coef) => [
    sqrtLatex(coef, 3),
    sqrtLatex(coef, 2),
  ]);

  const preferred = pairOptions.filter((latex) => latex !== correct);
  const fallback = [
    String(a),
    sqrtLatex(a + 1, 3),
    sqrtLatex(a + 1, 2),
    sqrtLatex(Math.max(1, a - 1), 3),
    sqrtLatex(Math.max(1, a - 1), 2),
  ];

  const seen = new Set([correct]);
  return [...preferred, ...fallback].filter((latex) => {
    if (seen.has(latex)) return false;
    seen.add(latex);
    return true;
  }).slice(0, 3);
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 26:
 *   objętość sześcianu = 729 → krawędź a=9 → przekątna d = 9√3  (A).
 */
function createMay2025OriginalTask() {
  const options = [
    { latex: "9\\sqrt{3}", isCorrect: true, label: "A" },
    { latex: "9\\sqrt{2}", isCorrect: false, label: "B" },
    { latex: "3\\sqrt{3}", isCorrect: false, label: "C" },
    { latex: "3\\sqrt{2}", isCorrect: false, label: "D" },
  ];

  return {
    type: "MaturaCubeVolumeDiagonalClosedTask",
    data: {
      a: 9,
      volume: 729,
      correctLatex: "9\\sqrt{3}",
      baseDiagonalLatex: "9\\sqrt{2}",
      options,
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 26,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const a = pickOne(EDGES);
  const volume = a ** 3;
  const correctLatex = sqrtLatex(a, 3);

  const rawOptions = [
    { latex: correctLatex, isCorrect: true },
    ...makeDistractors(a).map((latex) => ({ latex, isCorrect: false })),
  ];

  const options = shuffle(rawOptions).map((opt, idx) => ({
    ...opt,
    label: String.fromCharCode(65 + idx),
  }));
  const correctIndex = options.findIndex((opt) => opt.isCorrect);

  return {
    type: "MaturaCubeVolumeDiagonalClosedTask",
    data: {
      a,
      volume,
      correctLatex,
      baseDiagonalLatex: sqrtLatex(a, 2),
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { volume, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Objętość sześcianu jest równa <InlineMath math={String(volume)} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>Długość przekątnej tego sześcianu jest równa</p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    !!userAnswer &&
    typeof userAnswer.choiceIndex === "number" &&
    userAnswer.choiceIndex === data.correctIndex
  );
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
}

export function renderExplanation({ data }) {
  const { a, volume, baseDiagonalLatex, correctLatex } = data;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1. Obliczamy długość krawędzi sześcianu.</strong>
      </p>
      <p>
        Jeśli krawędź sześcianu oznaczymy przez <InlineMath math={"a"} />,
        to jego objętość jest równa <InlineMath math={"a^3"} />.
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`a^3=${volume}`} />
        <BlockMath math={`a=${a}`} />
      </div>

      <p>
        <strong>Krok 2. Liczymy przekątną podstawy.</strong>
      </p>
      <p>
        Podstawą sześcianu jest kwadrat o boku <InlineMath math={String(a)} />.
        Z twierdzenia Pitagorasa:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`p^2=${a}^2+${a}^2=2\\cdot ${a}^2`} />
        <BlockMath math={`p=${baseDiagonalLatex}`} />
      </div>

      <p>
        <strong>Krok 3. Liczymy przekątną sześcianu.</strong>
      </p>
      <p>
        Przekątna sześcianu, przekątna podstawy i pionowa krawędź sześcianu
        tworzą trójkąt prostokątny.
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`d^2=\\left(${baseDiagonalLatex}\\right)^2+${a}^2`} />
        <BlockMath math={`d^2=2\\cdot ${a}^2+${a}^2=3\\cdot ${a}^2`} />
        <BlockMath math={`d=${correctLatex}`} />
      </div>
    </div>
  );
}
